import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Loader2, Plus, Search, Pencil, Trash2, FileText, ExternalLink } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetAdminBlogsQuery, useDeleteBlogMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { BlogListItem } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'A';
}

function formatDate(iso: string | null): string {
    if (!iso) return 'Not set';
    return new Date(iso).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatusBadge({ status }: { status: BlogListItem['status'] }) {
    const published = status === 'published';
    return (
        <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                published ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}
        >
            {published ? 'Published' : 'Draft'}
        </span>
    );
}

export default function BlogAdmin() {
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [error, setError] = useState('');

    const params = useMemo(() => {
        const p: { page: number; limit: number; search?: string } = { page, limit: 20 };
        if (search) p.search = search;
        return p;
    }, [page, search]);

    const { data, isLoading, isFetching } = useGetAdminBlogsQuery(params);
    const [deleteBlog] = useDeleteBlogMutation();

    const items = data?.items ?? [];
    const totalPages = data?.totalPages ?? 1;

    const runSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput.trim());
    };

    const handleDelete = async (post: BlogListItem) => {
        if (!window.confirm(`Delete “${post.title}”? This also removes its cover and document files.`)) return;
        setError('');
        setDeletingId(post.id);
        try {
            await deleteBlog(post.id).unwrap();
        } catch (err) {
            setError(parseErrorMessage(err, 'Could not delete this post.'));
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <main className="flex min-h-screen w-full flex-col bg-[#EDEFF3] px-4 py-6 md:px-6 lg:px-8 xl:px-12">
            <div className="flex flex-1 flex-col overflow-hidden rounded-3xl bg-white shadow-sm">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Admin'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'A'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex w-full flex-1 flex-col gap-6 overflow-y-auto p-4 md:p-8 lg:p-10">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 md:text-3xl lg:text-4xl">Blog</h1>
                            <p className="mt-1 text-sm text-gray-500">
                                Write articles and share documents. Drafts stay private until you publish them.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => navigate('/admin/blog/new')}
                            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#FF6B35] px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#ff5722]"
                        >
                            <Plus className="h-4 w-4" /> New post
                        </button>
                    </div>

                    <form onSubmit={runSearch} className="flex max-w-md items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            <input
                                type="search"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Search by title or excerpt"
                                className="w-full rounded-full border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 outline-none focus:border-[#FF6B35]"
                            />
                        </div>
                        <button
                            type="submit"
                            className="rounded-full bg-black px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-900"
                        >
                            Search
                        </button>
                    </form>

                    {error && (
                        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {isLoading ? (
                        <div className="flex items-center justify-center py-20">
                            <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="py-20 text-center">
                            <FileText className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                            <p className="text-gray-500">{search ? 'No posts match your search.' : 'No posts yet.'}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-2xl border border-gray-100">
                            <table className="w-full min-w-[760px] border-collapse text-left text-sm">
                                <thead>
                                    <tr className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                                        <th className="px-4 py-3 font-semibold">Title</th>
                                        <th className="px-4 py-3 font-semibold">Type</th>
                                        <th className="px-4 py-3 font-semibold">Status</th>
                                        <th className="px-4 py-3 font-semibold">Views</th>
                                        <th className="px-4 py-3 font-semibold">Published</th>
                                        <th className="px-4 py-3 text-right font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((post) => (
                                        <tr key={post.id} className="border-b border-gray-50 last:border-0">
                                            <td className="px-4 py-3">
                                                <button
                                                    type="button"
                                                    onClick={() => navigate(`/admin/blog/${post.id}`)}
                                                    className="text-left font-medium text-gray-900 hover:text-[#FF6B35]"
                                                >
                                                    {post.title}
                                                </button>
                                                {post.tags && post.tags.length > 0 && (
                                                    <div className="mt-1 flex flex-wrap gap-1">
                                                        {post.tags.slice(0, 3).map((tag) => (
                                                            <span
                                                                key={tag}
                                                                className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 capitalize text-gray-600">{post.contentType}</td>
                                            <td className="px-4 py-3">
                                                <StatusBadge status={post.status} />
                                            </td>
                                            <td className="px-4 py-3 text-gray-600">{post.viewCount}</td>
                                            <td className="px-4 py-3 text-gray-600">{formatDate(post.publishedAt)}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-end gap-1">
                                                    {post.status === 'published' && (
                                                        <a
                                                            href={`/blog/${post.slug}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title="View live"
                                                            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                                        >
                                                            <ExternalLink className="h-4 w-4" />
                                                        </a>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/admin/blog/${post.id}`)}
                                                        title="Edit"
                                                        className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(post)}
                                                        disabled={deletingId === post.id}
                                                        title="Delete"
                                                        className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                                    >
                                                        {deletingId === post.id ? (
                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="h-4 w-4" />
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-3">
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                disabled={page <= 1 || isFetching}
                                className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-500">
                                Page {page} of {totalPages}
                            </span>
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages || isFetching}
                                className="rounded-full border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}
